import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Small, defensive boundary for the Android notification implementation.
 *
 * The native class is deliberately not part of the web bundle contract.  On
 * browsers this proxy is inert; on Android it becomes available only after
 * `MainActivity` registers a plugin named `AthenaNotifications`.
 *
 * Expected native surface for Fase 3:
 * - getStatus() -> { permission, push?, alarms?, deviceId?, platform?, appVersion?, androidVersion? }
 * - requestPermission()
 * - getToken() -> { token, deviceId? } (or a token string)
 * - clearIdentity(), scheduleAlarm(), cancelAlarm(), getPendingEvents()
 * - events: tokenRefresh, notificationForeground, notificationAction
 *
 * Compatibility aliases are intentionally tolerated while the native plugin
 * is being introduced. The Java implementation remains the authority.
 */
const AthenaNotificationsNative = registerPlugin('AthenaNotifications');

const noop = () => {};

function normalizePermission(value) {
  if (typeof value !== 'string') return 'unknown';
  const normalized = value.trim().toLowerCase();
  if (normalized === 'granted') return 'granted';
  if (normalized === 'denied') return 'denied';
  if (normalized === 'prompt' || normalized === 'prompt-with-rationale') return 'default';
  return normalized || 'unknown';
}

async function callNative(plugin, methodNames, args = []) {
  for (const methodName of methodNames) {
    try {
      const method = plugin?.[methodName];
      if (typeof method !== 'function') continue;
      return await method.apply(plugin, args);
    } catch {
      // Try the next known method name. This keeps an old APK harmless while
      // a newer web bundle is already installed.
    }
  }
  return null;
}

function readPermission(result) {
  return normalizePermission(
    result?.permission
      || result?.notifications
      || result?.notification
      || result?.postNotifications,
  );
}

function nativeAlarmId(value) {
  const id = typeof value === 'string' ? value.trim() : '';
  return /^[A-Za-z0-9._:-]{1,120}$/.test(id) ? id : '';
}

function nativeAlarmText(value, maximum) {
  if (typeof value !== 'string') return '';
  return value.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, maximum);
}

function nativeAlarmTimestamp(value) {
  const timestamp = Number(value);
  return Number.isFinite(timestamp) && timestamp > Date.now() ? Math.floor(timestamp) : 0;
}

export function isNativeNotificationsPluginAvailable() {
  try {
    return Capacitor.isNativePlatform()
      && Capacitor.getPlatform() === 'android'
      && Capacitor.isPluginAvailable('AthenaNotifications');
  } catch {
    return false;
  }
}

export function getNativeNotificationsPlugin() {
  return isNativeNotificationsPluginAvailable() ? AthenaNotificationsNative : null;
}

export async function getNativeNotificationsCapabilities() {
  const plugin = getNativeNotificationsPlugin();
  if (!plugin) {
    return {
      native: false,
      push: false,
      pushEnabled: false,
      notifications: false,
      alarms: false,
      permission: 'unavailable',
    };
  }

  try {
    const capabilities = await callNative(plugin, ['getStatus', 'getCapabilities']);
    // `getStatus` is the Phase 3 contract.  A plugin that reports a
    // permission state but omits the optional `notifications` capability is
    // still a valid notification plugin (notably before Android 13's prompt
    // has been answered).  Treat an explicit false as unsupported; do not
    // mistake "not granted yet" for "plugin unavailable".
    const hasStatus = Boolean(capabilities && typeof capabilities === 'object');
    return {
      native: true,
      push: capabilities?.push === true || capabilities?.pushEnabled === true,
      pushEnabled: capabilities?.pushEnabled === true || capabilities?.enabled === true || capabilities?.registered === true,
      notifications: capabilities?.notifications !== false && hasStatus,
      alarms: capabilities?.alarms === true,
      permission: readPermission(capabilities),
    };
  } catch {
    return {
      native: true,
      push: false,
      pushEnabled: false,
      notifications: false,
      alarms: false,
      permission: 'unknown',
    };
  }
}

export async function getNativeNotificationPermission() {
  const plugin = getNativeNotificationsPlugin();
  if (!plugin) return { available: false, permission: 'unavailable' };

  try {
    const result = await callNative(plugin, ['getStatus', 'checkPermissions']);
    const permission = readPermission(result);
    return { available: true, permission };
  } catch {
    return { available: true, permission: 'unknown', error: 'permission_check_failed' };
  }
}

export async function requestNativeNotificationPermission() {
  const plugin = getNativeNotificationsPlugin();
  if (!plugin) return { available: false, permission: 'unavailable' };

  const current = await getNativeNotificationPermission();
  if (current.permission === 'granted' || current.permission === 'denied') return current;

  try {
    // The Athena Android plugin deliberately exposes `requestPermission()`.
    // The compatibility alias follows the Capacitor Push Notifications shape.
    // Keeping their argument contracts separate avoids making a strict native
    // implementation reject the canonical call.
    let result;
    if (typeof plugin.requestPermission === 'function') {
      result = await plugin.requestPermission();
    } else {
      result = await callNative(plugin, ['requestPermissions'], [{ permissions: ['notifications'] }]);
    }
    const permission = readPermission(result);
    // A native method may resolve without a payload. Re-read status so the
    // React UI still reflects the user's choice without trying to request a
    // second time.
    if (permission === 'unknown') return getNativeNotificationPermission();
    return { available: true, permission };
  } catch {
    return { available: true, permission: 'unknown', error: 'permission_request_failed' };
  }
}

export async function getNativePushRegistration() {
  const plugin = getNativeNotificationsPlugin();
  if (!plugin) return null;

  try {
    const [tokenResult, status] = await Promise.all([
      callNative(plugin, ['getToken', 'getPushRegistration']),
      callNative(plugin, ['getStatus', 'getCapabilities']),
    ]);
    const tokenSource = typeof tokenResult === 'string' ? { token: tokenResult } : (tokenResult || {});
    const token = typeof tokenSource?.token === 'string'
      ? tokenSource.token.trim()
      : (typeof tokenSource?.pushToken === 'string' ? tokenSource.pushToken.trim() : '');
    const deviceId = typeof tokenSource?.deviceId === 'string'
      ? tokenSource.deviceId.trim()
      : (typeof tokenSource?.device_id === 'string'
        ? tokenSource.device_id.trim()
        : (typeof status?.deviceId === 'string'
          ? status.deviceId.trim()
          : (typeof status?.device_id === 'string' ? status.device_id.trim() : '')));
    if (!token || !deviceId) return null;
    return {
      token,
      deviceId,
      platform: typeof tokenSource?.platform === 'string'
        ? tokenSource.platform
        : (typeof status?.platform === 'string' ? status.platform : 'android-native'),
      appVersion: typeof tokenSource?.appVersion === 'string'
        ? tokenSource.appVersion
        : (typeof status?.appVersion === 'string' ? status.appVersion : undefined),
      androidVersion: typeof tokenSource?.androidVersion === 'string'
        ? tokenSource.androidVersion
        : (typeof status?.androidVersion === 'string' ? status.androidVersion : undefined),
    };
  } catch {
    return null;
  }
}

/**
 * Local Android alarm bridge. It does not create or mutate the server
 * reminder; callers must persist the server record first and treat this as a
 * best-effort device mirror.
 */
export async function scheduleNativeAthenaAlarm({
  alarmId,
  notificationId,
  triggerAt,
  title = 'Despertador da Athena',
  body = '',
  route = '/',
  exact = true,
} = {}) {
  const plugin = getNativeNotificationsPlugin();
  if (!plugin) return { scheduled: false, reason: 'native_plugin_unavailable' };

  const id = nativeAlarmId(alarmId);
  const notification_id = nativeAlarmId(notificationId || alarmId);
  const at = nativeAlarmTimestamp(triggerAt);
  if (!id || !notification_id || !at) return { scheduled: false, reason: 'invalid_alarm_input' };

  try {
    const result = await callNative(plugin, ['scheduleAlarm'], [{
      id,
      alarmId: id,
      alarm_id: id,
      notificationId: notification_id,
      notification_id,
      triggerAt: at,
      trigger_at: at,
      title: nativeAlarmText(title, 120) || 'Despertador da Athena',
      body: nativeAlarmText(body, 360),
      route: nativeAlarmText(route, 600) || '/',
      kind: 'alarm',
      exact: exact !== false,
      // Native decides whether an inexact fallback is available; the UI must
      // never make server reminder creation depend on exact-alarm permission.
      requireExact: false,
      require_exact: false,
    }]);
    if (!result || typeof result !== 'object') return { scheduled: false, reason: 'native_alarm_no_result' };
    return {
      ...result,
      scheduled: result.scheduled === true,
      alarmId: id,
      notificationId: notification_id,
    };
  } catch {
    return { scheduled: false, reason: 'native_alarm_schedule_failed', alarmId: id, notificationId: notification_id };
  }
}

/**
 * Reads the Android-side alarm registry after a schedule call. Scheduling is
 * not treated as evidence by itself: this lets the conversation layer verify
 * that the exact reminder identity was actually retained by the device.
 */
export async function listNativeAthenaAlarms() {
  const plugin = getNativeNotificationsPlugin();
  if (!plugin) return { available: false, alarms: [] };
  try {
    const result = await callNative(plugin, ['listAlarms']);
    const alarms = Array.isArray(result?.alarms)
      ? result.alarms.map((alarm) => ({
        id: nativeAlarmId(alarm?.id || alarm?.alarmId || alarm?.alarm_id),
        triggerAt: Number(alarm?.triggerAt || alarm?.trigger_at || 0) || 0,
        scheduled: alarm?.scheduled !== false,
        status: nativeAlarmText(alarm?.status || '', 80),
      })).filter((alarm) => alarm.id)
      : [];
    return { available: true, alarms };
  } catch {
    return { available: true, alarms: [], status: 'native_alarm_registry_unavailable' };
  }
}

async function invokeNativeAlarmAction(method, alarmId, extra = {}, successField) {
  const plugin = getNativeNotificationsPlugin();
  if (!plugin) return { ok: false, reason: 'native_plugin_unavailable' };
  const id = nativeAlarmId(alarmId);
  if (!id) return { ok: false, reason: 'invalid_alarm_id' };
  try {
    const result = await callNative(plugin, [method], [{ id, alarmId: id, alarm_id: id, ...extra }]);
    if (!result || typeof result !== 'object') return { ok: false, reason: 'native_alarm_no_result', alarmId: id };
    return { ...result, ok: successField ? result[successField] === true : true, alarmId: id };
  } catch {
    return { ok: false, reason: 'native_alarm_action_failed', alarmId: id };
  }
}

export function cancelNativeAthenaAlarm(alarmId) {
  return invokeNativeAlarmAction('cancelAlarm', alarmId, {}, 'cancelled');
}

export function snoozeNativeAthenaAlarm(alarmId, minutes = 5) {
  const safeMinutes = Number(minutes) === 10 ? 10 : 5;
  return invokeNativeAlarmAction('snoozeAlarm', alarmId, {
    minutes: safeMinutes,
    snoozeMinutes: safeMinutes,
    snooze_minutes: safeMinutes,
  }, 'scheduled');
}

export function dismissNativeAthenaAlarm(alarmId) {
  return invokeNativeAlarmAction('dismissAlarm', alarmId, {}, 'dismissed');
}

/**
 * Drains the complete native event queue once. The Android implementation
 * deliberately persists events before the WebView is ready, so selecting only
 * the launch action here would silently lose displayed/foreground/snooze
 * lifecycle signals needed by the Worker and notification center.
 */
export async function getNativePendingNotificationEvents() {
  const plugin = getNativeNotificationsPlugin();
  if (!plugin) return [];
  try {
    const pending = await callNative(plugin, ['getPendingEvents', 'drainEvents', 'getLaunchNotificationAction']);
    const events = Array.isArray(pending)
      ? pending
      : (Array.isArray(pending?.events)
        ? pending.events
        : (Array.isArray(pending?.pendingEvents) ? pending.pendingEvents : null));
    if (events) return events;
    // Compatibility with a plugin that exposes one flattened launch event.
    if (pending?.event || pending?.type || pending?.notification_id || pending?.notificationId) return [pending];
    return [];
  } catch {
    return [];
  }
}

export async function addNativeNotificationsListener(eventName, listener) {
  const plugin = getNativeNotificationsPlugin();
  if (!plugin || typeof listener !== 'function') return noop;

  try {
    const handle = await plugin.addListener(eventName, listener);
    return () => {
      try {
        const removal = handle?.remove?.();
        if (removal?.catch) removal.catch(noop);
      } catch {
        // WebView teardown can invalidate a native listener before unmount.
      }
    };
  } catch {
    return noop;
  }
}
